import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAt } from '@fortawesome/free-solid-svg-icons/faAt';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { faHouse } from '@fortawesome/free-solid-svg-icons/faHouse';
import { personHref, staff_title_long, trans } from 'components/langutils';
import { useData } from '../Data';
import { StaffConference } from './Conference';
import { MapButton, MapImage, useStaffPlaces } from './Map';

const useSortableData = (items: any[], config: any = null) => {
  const [sortConfig, setSortConfig] = React.useState(config);
  
  const sortedItems = React.useMemo(() => {
    const sortableItems = [...items];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key: any) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  }

  return { items: sortedItems, requestSort, sortConfig };
}

export function StaffRoom({user}: any) {
  const { lang } = useData();
  const { building, floor } = useStaffPlaces(user);
  const t = trans(lang);
  if (!user.building) {
    return null;
  }
  return (<>
    <FontAwesomeIcon icon={faHouse} style={{color: 'var(--iti-accent)',}} />&nbsp;
    {building?.image
      ? <MapButton text={user.building} label={t.show_on_site_plan}
          map={<MapImage src={building.image} label={`${t.site_plan}: ${t.building} ${user.building}`} />} />
      : user.building}
    {user.room && <>,&nbsp;
      {floor?.image
        ? <MapButton text={user.room} label={t.show_on_floor_plan}
            map={<MapImage src={floor.image} highlight={`room${user.room}`} label={`${t.floor_plan}: ${t.room} ${user.room}`} />} />
        : user.room}
    </>}
  </>);
}

export function StaffTable({pos, alumni} : any) {
  const { lang, setHover, hover } = useData();
  const { items, requestSort, sortConfig } = useSortableData(pos.users, {key: 'last_name', direction: 'ascending'});
  const getChevronFor = (name: string) => {
    if (!sortConfig) {
      return "";
    }
    return sortConfig.key === name ? (sortConfig.direction === 'ascending'? '▲' : '▼') : "";
  };
  return (
    <div>
      <h2>{pos[`title_${lang}_plural`] || pos[`title_${lang}`] || pos.title_en}</h2>
      <div className="tx-wtdirectory-pi1">
        <table className="table table-striped table-hover wtdirectory_table_wrap" width="100%">
          <thead>
            <th className="th-1">
              <span onClick={() => requestSort('first_name')}>{trans(lang).first_name}{getChevronFor('first_name')}</span>
              &nbsp;
              <span onClick={() => requestSort('last_name')}>{trans(lang).last_name}{getChevronFor('last_name')}</span>
            </th>
            { alumni ||
              <>
                <th className="th-2">
                  <span onClick={() => requestSort('email')}>Email{getChevronFor('email')}</span>
                </th>
                <th className="th-3">
                  <span onClick={() => requestSort('telephone')}>Tel.{getChevronFor('telephone')}</span>
                </th>
                <th className="th-4">{ trans(lang).room }</th>
              </>
            }
          </thead>
          <tbody>
            {items.map((user: any) =>
              <tr
                key={user.login}
                onMouseEnter={() => setHover(user.login) }
                onMouseLeave={() => setHover(null)}
                style={{
                  boxShadow: hover===user.login? '0 0 10px #0096af' : '',
                  position: hover===user.login? 'relative' : 'static',
                  zIndex: hover===user.login? 10 : 0,
                }}
              >
                <td className="td-1">
                  { ((alumni && user.website) || !alumni) &&
                    (
                      <a href={alumni && user.website || personHref(user, lang)}>{`${user.full_name}`}</a>
                    ) || `${user.full_name}`
                  }
                </td>
                { alumni ||
                  <>
                    <td className="td-2">
                      <FontAwesomeIcon icon={faAt} style={{color: 'var(--iti-accent)',}} />&nbsp;
                      <a href={`mailto:${user.email}`}>{user.email}</a>
                    </td>
                    <td className="td-3">
                      <FontAwesomeIcon icon={faPhone} style={{color: 'var(--iti-accent)',}} />&nbsp;
                      <a href={`tel:${user.telephone}`}>{user.telephone}</a>
                    </td>
                    <td className="td-4">
                      <StaffRoom user={user} />
                    </td>
                  </>
                }
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>   
  )
}

export function StaffList({alumni}: any) {
  const { users, positions, lang, setHover, hover } = useData();
  const position = [...(positions as any[])];
  // the order the editors set for the team page (Position.order), then the id
  position.sort((a: any, b: any) => (a.order ?? 100) - (b.order ?? 100) || a.id - b.id);
  position.forEach((pos: any) => {
    pos.users = [...users.filter((u: any) => u.position === pos.id && (u.active !== alumni))];
    pos.users.sort((a: any, b: any) => a.first_name.localeCompare(b.first_name));
  });
  return (
    <>
      <div className="csc-default">
        <div className="csc-header csc-header-n1">
          <h1 className="csc-firstHeader">{staff_title_long(lang)}</h1>
        </div>
      </div>
      {alumni ||
        <StaffConference />
      }
      {position.map((pos: any) => pos.users.length === 0 ? <div key={pos.id}></div> :
        <StaffTable key={pos.id} pos={pos} alumni={alumni} />
      )}
    </>
  );
}
